import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import * as types from '../../../constants/actionTypes';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { TASK_COMPLETION_SLICES, HEATMAP_VIEWS, DATE_FORMAT, MOBILE_WINDOW_BREAKPOINT, FONT_FAMILY } from '../../../constants/constants';
import { actions as companyDashboardActions } from '../../../actions/companyDashboardActions';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import Chart from 'react-apexcharts';
import Button from '../../Common/Button';
import Notification from '../../Common/Notification';
import WorkloadDetailsDrawer from './WorkloadDetailsDrawer';
import Moment from 'moment';
import './Workload.scss';

const MAX_PAGE_NUMBER = 10;
const MIN_PAGE_NUMBER = -4;

export class Workload extends Component {

    constructor(props, context) {
        super(props, context);
        this._isMounted = false;

        this.state = {
            locations: [],
            isLoading: false,
            isLoadingDetails: false,
            selectedView: "month",
            selectedDivider: "All",
            selectedSecondaryDivider: "",
            currentStartDate: null,
            showDetailsDrawer: false,
            // click tracking
            lastClickedSeriesName: "",
            lastClickedDateStart: "",
            lastClickedDateEnd: "",
            series: [{
                data: [{
                    x: 'Apple',
                    y: 54
                }, {
                    x: 'Orange',
                    y: 66
                }],
            }],

            options: {
                yaxis: {
                    reversed: true,
                    labels: {
                        style: {
                            fontFamily: FONT_FAMILY
                        }
                    }
                },
                xaxis: {
                    minHeight: 30,
                    maxHeight: 30,
                    labels: {
                        style: {
                            fontFamily: FONT_FAMILY
                        }
                    }
                },
                chart: {
                    events: {
                        click: this.heatmap_click
                    },
                    animations: {
                        enabled: true,
                        speed: 250,
                        animateGradually: {
                            enabled: true,
                            delay: 100
                        },
                        dynamicAnimation: {
                            enabled: true,
                            speed: 100
                        }
                    }
                },
                plotOptions: {
                    heatmap: {
                        distributed: true
                    }
                },
                colors: ["#00a4fb"],
                dataLabels: {
                    style: {
                        colors: ["#666"],
                        fontFamily: FONT_FAMILY
                    },
                    formatter: function (val) {
                        // don't display 0's in the squares
                        return (val == 0) ? "" : val;
                    }
                }
            },
        }
    }

    componentDidMount = () => {
        this._isMounted = true;
        this.search(1);
    }

    componentWillUnmount = () => {
        this._isMounted = false;
    };

    componentDidUpdate = async (prevProps) => {
        if (this.props.settings && (this.props.settings !== prevProps.settings)) {
            await this.search(1);
        }
    }

    isMobile = () => {
        return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
    }

    search = async (pageNumber) => {
        try {
            if (this._isMounted) this.setState({ isLoading: true });

            const response = await this.props.companyDashboardActions.loadWorkload(this.props.settings, this.state.selectedView, this.state.selectedDivider, this.state.selectedSecondaryDivider, pageNumber);
            if (response.type === types.LOAD_WORKLOAD_RESULTS_SUCCESS && this._isMounted) {

                this.setState({
                    series: response.payload.seriesData,
                    isLoading: false,
                    pageNumber: pageNumber,
                    startDateRange: response.payload.startDateRange,
                    endDateRange: response.payload.endDateRange,
                    isLastPage: response.payload.isLastPage
                });

            } else if (response.type === types.LOAD_WORKLOAD_RESULTS_FAILURE && this._isMounted) {
                Notification("error", "Error", response.payload.message);
            }

        } catch (error) {
            if (this._isMounted) Notification("error", "Error", error);
        }
    }

    getLocations = async (seriesName, seriesUserKey, startDate, endDate) => {
        try {
            this.setState({ isLoadingDetails: true, showDetailsDrawer: true });
            let searchParams = {
                ...this.props.settings,
                targetDateStart: startDate,
                targetDateEnd: endDate
            };
            const response = await this.props.companyDashboardActions.getWorkloadDetails(searchParams, this.state.selectedDivider, seriesUserKey || seriesName);
            if (response.type === types.GET_WORKLOAD_DETAILS_SUCCESS) {

                this.setState({
                    locations: response.payload.locations,
                    lastClickedSeriesName: seriesName,
                    lastClickedDateStart: startDate.format(DATE_FORMAT),
                    lastClickedDateEnd: endDate.format(DATE_FORMAT)
                });

            } else if (response.type === types.GET_WORKLOAD_DETAILS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }

        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoadingDetails: false });
        }
    }

    heatmap_click = (event, chartContext, config) => {
        // figure out date range, make api call to get details info, open drawer
        if (config.seriesIndex != -1 && config.dataPointIndex != -1) {
            let x = this.state.series[config.seriesIndex].data[config.dataPointIndex].x;
            let seriesName = this.state.series[config.seriesIndex].name;
            let seriesUserKey = this.state.series[config.seriesIndex].responsibleUserKey || "";
            // build a date range for the clicked square based on selected view
            let start;
            let end;
            switch (this.state.selectedView) {
                case HEATMAP_VIEWS.Month.value:
                    // MM YYYY
                    start = Moment("1-" + x, "DD-MMM YYYY");
                    end = Moment(start).add(1, "month");
                    break;
                case HEATMAP_VIEWS.Week.value:
                    start = Moment(x, "MMM DD YYYY");
                    end = Moment(start).add(1, "week");
                    break;
                case HEATMAP_VIEWS.Day.value:
                    start = Moment(x, "MMM DD YYYY");
                    end = Moment(start).add(1, "day");
                    break;
            }
            this.getLocations(seriesName, seriesUserKey, start, end);
        }
    }

    onChangeView = (view) => {
        this.setState({ selectedView: view, isLoading: true, currentStartDate: null }, () => this.search(1));
    }

    onDropdownChange = (csv, name) => {
        // make sure to reset currentStartDate when the view changes
        if (csv === TASK_COMPLETION_SLICES.ResponsibleUser) {
            this.setState({ selectedSecondaryDivider: this.getSecondaryOptions()[0].name });
        }
        this.setState({ [name]: csv, isLoading: true, currentStartDate: null }, () => this.search(1));
    }

    onNavPrev = () => {
        //Allowing user to go back 5 times
        if (this.state.pageNumber !== MIN_PAGE_NUMBER) {
            this.setState({ isLoading: true }, () => this.search(this.state.pageNumber - 1));
        }
    }

    onCloseDetailsDrawer = () => {
        this.setState({ showDetailsDrawer: false });
    }

    onNavNext = () => {
        if (this.state.pageNumber < MAX_PAGE_NUMBER) {
            this.setState({ isLoading: true }, () => this.search(this.state.pageNumber + 1));
        }
    }

    getRangeDisplay = () => {
        return Moment(this.state.startDateRange).add(1, "day").format(DATE_FORMAT) + " - " + Moment(this.state.endDateRange).format(DATE_FORMAT);
    }

    getHeight = () => {
        // a calculation that makes the chart rows always the same height no matter the number of series
        return 100 + (this.state.series.length * 30);
    }

    getSecondaryOptions = () => {
        return this.props.filters.disciplines.map(item => ({ id: item.name, name: item.name }));
    }

    render() {
        const dividedOptions = [
            { id: "All", name: "All Uncompleted Tasks" },
            { id: TASK_COMPLETION_SLICES.Brand, name: TASK_COMPLETION_SLICES.Brand },
            { id: TASK_COMPLETION_SLICES.Continent, name: TASK_COMPLETION_SLICES.Continent },
            { id: TASK_COMPLETION_SLICES.Department, name: TASK_COMPLETION_SLICES.Department },
            { id: TASK_COMPLETION_SLICES.Discipline, name: TASK_COMPLETION_SLICES.Discipline },
            { id: TASK_COMPLETION_SLICES.ResponsibleUser, name: TASK_COMPLETION_SLICES.ResponsibleUser }
        ];

        if (this.props.accessibleLocationCount <= 500) {
            dividedOptions.push({ id: TASK_COMPLETION_SLICES.Location, name: TASK_COMPLETION_SLICES.Location });
        }

        dividedOptions.sort((a, b) => (a.name > b.name) ? 1 : -1)

        return (
            <Fragment>
                <div className="row">
                    <div className="col-12">
                        <div className="dashboard-widget-title">Workload</div>
                        <div className="dashboard-widget-subtitle">How tasks are distributed for all of your teams</div>
                    </div>
                </div>
                <LoadingIndicator isLoading={this.state.isLoading} >
                    <div className="workload-widget">
                        <div className="row mb-3">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="selectedDivider" idField="id" valueField="name"
                                    label=""
                                    className="view-selector col-sm-12 col-md-5"
                                    options={dividedOptions}
                                    onChange={this.onDropdownChange}
                                    selectedValues={this.state.selectedDivider}
                                    isMulti={false}
                                    isSearchable={false}
                                />

                                {this.state.selectedDivider === TASK_COMPLETION_SLICES.ResponsibleUser &&
                                    <>
                                        {!this.isMobile() && <div className="view-selector-text col-md-2">By</div>}
                                        <MultiSelectBox name="selectedSecondaryDivider" idField="id" valueField="name"
                                            label=""
                                            className="view-selector col-sm-12 col-md-5"
                                            options={this.getSecondaryOptions()}
                                            onChange={this.onDropdownChange}
                                            selectedValues={this.state.selectedSecondaryDivider}
                                            isMulti={false}
                                            isSearchable={false} />
                                    </>
                                }
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <div className="nav-controls">
                                    {this.state.pageNumber !== MIN_PAGE_NUMBER &&
                                        <Button type="button" iconName="chevron-left" bootstrapClass="light button-prev" onClick={this.onNavPrev} />
                                    }
                                    <span className="date-range">{this.getRangeDisplay()}</span>
                                    {!this.state.isLastPage &&
                                        <Button type="button" iconName="chevron-right" bootstrapClass="light button-next" onClick={this.onNavNext} />
                                    }
                                </div>
                            </div>
                            <div className="col-sm-12 col-md-2">

                            </div>
                            {!this.isMobile() &&
                                <div className="col-md-2">
                                    <div className="btn-group float-end">
                                        <button type="button" className={(this.state.selectedView === HEATMAP_VIEWS.Month.value) ? "btn btn-secondary" : "btn btn-light"} onClick={() => this.onChangeView(HEATMAP_VIEWS.Month.value)}>{HEATMAP_VIEWS.Month.label}</button>
                                        <button type="button" className={(this.state.selectedView === HEATMAP_VIEWS.Week.value) ? "btn btn-secondary" : "btn btn-light"} onClick={() => this.onChangeView(HEATMAP_VIEWS.Week.value)}>{HEATMAP_VIEWS.Week.label}</button>
                                        <button type="button" className={(this.state.selectedView === HEATMAP_VIEWS.Day.value) ? "btn btn-secondary" : "btn btn-light"} onClick={() => this.onChangeView(HEATMAP_VIEWS.Day.value)}>{HEATMAP_VIEWS.Day.label}</button>
                                    </div>
                                </div>
                            }

                        </div>

                        {!this.state.isLoading && this.state.series &&
                            <div className={this.isMobile() ? "is-mobile" : ""}>
                                <Chart type="heatmap" options={this.state.options} series={this.state.series} width="100%" height={this.getHeight()} />
                            </div>
                        }
                        {!this.state.series &&
                            <div className="mt-3">No Results Found</div>
                        }
                    </div>
                </LoadingIndicator>
                {this.state.showDetailsDrawer &&
                    <WorkloadDetailsDrawer
                        seriesName={this.state.lastClickedSeriesName}
                        dateRangeStart={this.state.lastClickedDateStart}
                        dateRangeEnd={this.state.lastClickedDateEnd}
                        locations={this.state.locations}
                        onComplete={this.onCloseDetailsDrawer}
                        companyDashboardActions={this.props.companyDashboardActions}
                        isLoading={this.state.isLoadingDetails}
                    />
                }
            </Fragment>
        )
    }
}

Workload.propTypes = {
    settings: PropTypes.object,
    filters: PropTypes.object.isRequired,
    windowWidth: PropTypes.number.isRequired,
    companyDashboardActions: PropTypes.object.isRequired,
    user: PropTypes.object,
    accessibleLocationCount: PropTypes.number.isRequired
};

function mapStateToProps(state) {
    return {
        windowWidth: state.window.width,
        user: state.user
    };
}

function mapDispatchToProps(dispatch) {
    return {
        companyDashboardActions: bindActionCreators(companyDashboardActions, dispatch)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Workload);